import i18n from "~/i18n"
import { defaultCtx } from "~/lib/AjaxCtx"
import { buildQueryFilters, downloadResponseFile } from "~/lib/utils"
import { getStartEndDates } from "~/modules/calendar/utils"
import {
    addDateAndExtentionToFileName,
    listKeyToFileName,
    urlForDownload,
} from "./utils"

function getDateFilters({ month, year, timeZone }) {
    const { startDate, endDate } = getStartEndDates({ month, year, timeZone })
    return buildQueryFilters({
        fromDate: startDate,
        toDate: endDate,
    })
}

export default class SupervisorService {
    constructor(ctx) {
        this.$http = ctx || defaultCtx
    }

    async getDashboard() {
        const dashboard = await this.$http.fetchData("supervisor/dashboard")
        dashboard.charts.forEach(c => (c.loading = true))
        return dashboard
    }

    async loadChart({ charts, title, url }) {
        if (charts.length) {
            return {
                charts: await Promise.all(
                    charts.map(chart => this.loadChart(chart))
                ),
                title,
            }
        } else {
            return await this.$http.fetchData(url)
        }
    }

    getPanel() {
        return this.$http.fetchData("supervisor/panel", {
            waitFor: "supervisor/panel",
        })
    }

    getCounters() {
        return this.$http.fetchData("supervisor/counters", {
            waitFor: "supervisor/counters",
        })
    }

    async getProgram(programId) {
        return await this.$http.fetchData(`supervisor/training/${programId}`)
    }

    async getUserCall(userCallId) {
        return await this.$http.fetchData(`supervisor/user-calls/${userCallId}`)
    }

    async getProgramCharts(programId) {
        const { charts } = await this.$http.fetchData(
            `supervisor/training/${programId}/charts`
        )
        const result = await this.loadChart({ charts })
        return result.charts
    }

    async getParticipant(userId) {
        return await this.$http.fetchData(`supervisor/participants/${userId}`)
    }

    async getRequest(url, id) {
        return await this.$http.fetchData(`${url}/${id}`)
    }

    async getProgramsRecipients(target, type) {
        const mode = type.replace("supervisor-programs-", "")
        return await this.$http.fetchData(
            `supervisor/training/${mode}/supervised`,
            {
                method: "POST",
                body: JSON.stringify({ target }),
            }
        )
    }

    async getSupervisedRecipients(target) {
        return await this.$http.fetchData("supervisor/participants/contact", {
            method: "POST",
            body: JSON.stringify({ target }),
        })
    }

    async getCallRecipients(target, type) {
        let programId = /\d+/.exec(type)
        if (!programId) throw new Error(`programId not found for type=${type}`)
        programId = programId[0]
        return await this.$http.fetchData(
            `supervisor/training/${programId}/supervised`,
            {
                method: "POST",
                body: JSON.stringify({ target }),
            }
        )
    }

    async getSupervisedCharts(userId) {
        const { charts } = await this.$http.fetchData(
            `supervisor/participants/${userId}/charts`
        )
        const result = await this.loadChart({ charts })
        return result.charts[0]
    }

    async getSupervisedEvents(userId, { month, year, timeZone }) {
        const filterField = getDateFilters({ month, year, timeZone })
        let url = `supervisor/participants/${userId}/events`
        if (filterField) {
            url = `${url}?filter=${filterField}`
        }
        return await this.$http.fetchData(url)
    }

    async getActivityDetails({ userId, userActivityId }) {
        return await this.$http.fetchData(
            `supervisor/participants/${userId}/participant-activity/${userActivityId}`
        )
    }

    async getUserCallEvents({ userCallId, userId, month, year, timeZone }) {
        const filterField = getDateFilters({ month, year, timeZone })
        let url = `supervisor/participants/${userId}/events/${userCallId}`
        if (filterField) {
            url = `${url}?filter=${filterField}`
        }
        return await this.$http.fetchData(url)
    }

    async editRequests(type, payload) {
        const segment = type.split("-")[1]
        const url = `supervisor/requests/${segment}/updateRequests`
        return await this.$http.fetchData(url, {
            method: "POST",
            body: JSON.stringify(payload),
        })
    }

    async download({ type, payload, fileName, onNotification }) {
        fileName = fileName
            ? addDateAndExtentionToFileName({
                  fileName,
                  extension: "csv",
              })
            : listKeyToFileName(type)
        const url = urlForDownload(type)
        const response = await this.$http.fetch(url, {
            method: "POST",
            body: JSON.stringify(payload),
        })
        this._handleDownloadResponse({ response, fileName, onNotification })
    }

    async downloadMonitoringReport({
        callFromDate,
        programs,
        unitBreakdown,
        onNotification,
    }) {
        let url = "supervisor/participants/monitoringReport"
        const fileName = listKeyToFileName("monitoring-report")
        const filterValues = {}

        if (callFromDate) {
            filterValues.callFromDate = {
                start: callFromDate.toISOString(),
            }
        }
        if (programs?.length > 0) {
            filterValues.programs = programs
        }
        const filterDescriptions = [
            { prop: "callFromDate", type: "date" },
            { prop: "programs", type: "multi-select" },
        ]
        const query = buildQueryFilters(filterValues, filterDescriptions)
        if (query) {
            url = `${url}?filter=${query}`
        }

        const response = await this.$http.fetch(url, {
            method: "POST",
            body: JSON.stringify({ unitBreakdown }),
        })
        this._handleDownloadResponse({ response, fileName, onNotification })
    }

    async downloadUserRecord({ userId, format, fileName, onNotification }) {
        const url = `supervisor/participants/${userId}/record/download/${format}`
        const response = await this.$http.fetch(url)
        this._handleDownloadResponse({ response, fileName, onNotification })
    }

    async _handleDownloadResponse({ response, fileName, onNotification }) {
        if (!response.ok) {
            throw new Error(i18n.global.t("errors.undefinedError"))
        }

        const contentType = response.headers.get("Content-Type")
        if (contentType === "application/json") {
            // This means the download is done using workers
            // so we just show a notification informing the user about it
            const {
                data: [message],
            } = await response.json()
            onNotification(message)
        } else {
            downloadResponseFile({ response, fileName, t: i18n.global.t })
        }
    }

    async signUp({ included, excluded, filter, groups }) {
        return await this.$http.fetchData("/supervisor/sign-up", {
            method: "POST",
            body: JSON.stringify({
                target: {
                    included,
                    excluded,
                    filter,
                },
                groups,
            }),
        })
    }
}
